import { useEffect, useRef } from 'react';

export interface KeyboardShortcut {
    /** Touche à presser, insensible à la casse (ex: 'm', '?') */
    key: string;
    /** Action déclenchée quand la touche est pressée */
    handler: () => void;
    /** Permet de désactiver un raccourci (droits insuffisants, données absentes...) */
    enabled?: boolean;
}

interface UseKeyboardShortcutsOptions {
    /** Désactive l'ensemble des raccourcis */
    enabled?: boolean;
}

const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

/** Les raccourcis ne doivent pas s'activer pendant la saisie dans un champ */
function isTyping(target: EventTarget | null): boolean {
    return target instanceof Element && target.closest(EDITABLE_SELECTOR) !== null;
}

/** Ni quand une modale, un menu ou une liste déroulante est ouvert */
function isOverlayOpen(): boolean {
    return document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]') !== null;
}

/**
 * Enregistre des raccourcis clavier globaux (une seule touche, sans modificateur).
 *
 * Les touches sont ignorées si l'utilisateur est en train de saisir du texte,
 * si une modale/menu est ouvert ou si une combinaison Ctrl/Cmd/Alt est utilisée.
 */
export function useKeyboardShortcuts(shortcuts: KeyboardShortcut[], options: UseKeyboardShortcutsOptions = {}) {
    const { enabled = true } = options;
    const shortcutsRef = useRef(shortcuts);

    useEffect(() => {
        shortcutsRef.current = shortcuts;
    }, [shortcuts]);

    useEffect(() => {
        if (!enabled) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            if (event.defaultPrevented || event.repeat) return;
            if (isTyping(event.target) || isOverlayOpen()) return;

            const shortcut = shortcutsRef.current.find(
                (item) => item.enabled !== false && item.key.toLowerCase() === event.key.toLowerCase(),
            );
            if (!shortcut) return;

            event.preventDefault();
            shortcut.handler();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [enabled]);
}
